import {
  useGetDashboardDaily,
  useGetDashboardRevenueChart,
  useGetDashboardStats,
} from '@agos/api-client/reporting'
import {
  T,
  formatCents,
  formatCount,
  formatPercent,
  tt,
  useAuth,
  useFranchiseModules,
} from '@agos/shared'
import { EmptyState, PageHeader, Spinner, StatCard, cn } from '@agos/ui'
import {
  AlertCircle,
  Banknote,
  CalendarDays,
  CreditCard,
  Percent,
  Receipt,
  ShoppingBag,
  Store,
  TrendingUp,
  Truck,
  Wallet,
  XCircle,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { busiestDayCents, channelsOf, chartIsEmpty, chartOf, dailyAscending, dailyMismatchNote, dailyOf, dayBarPercent, deliveryExpenseTile, discountShare, mayReadDashboard, paymentsOf, reconciliationNote, refusalNote, shareLabel, statsOf, type DayRow, type Stats } from './dashboard'
import {
  QUICK_PERIODS,
  formatRangeDate,
  isMultiDay,
  periodLabel,
  periodRange,
  rangeDays,
  rangeIsBackwards,
  rangeLabel,
  type PeriodKey,
} from './periods'

/**
 * `/` and `/dashboard` — the first screen anybody opens.
 *
 * Ported from legacy `pages/dashboard.tsx` (480 lines). Three endpoints, six presets and a custom range,
 * eleven KPI cards, an area chart and — on a multi-day range — a table of days.
 *
 * **FB-171.** Two presets ask the server for the wrong month, at every hour of every day. See `periods.ts`.
 *
 * **FB-172.** Four of five roles are shown this in the sidebar and served nothing. See `dashboard.ts`.
 *
 * **FB-173.** `deliveryCost`, `deliveryOrders`, `restaurantOrders` and `barOrders` are fetched and rendered
 * nowhere in legacy. All four are on the screen now.
 *
 * **FB-174.** The third channel is *BAR* in `sales_channels` and *Osobný odber* on legacy's cards.
 *
 * The eleven cards, the chart and the day table each sit behind a `dashboard.*` franchise module, as they do
 * in legacy — the keys are `kpi.revenue`, `kpi.orders`, `kpi.aov`, `kpi.discounts`, `kpi.cancellations`,
 * `kpi.channels`, `kpi.payments`, `chart_revenue`, `payment_methods_split` and `daily_table`. Three further
 * `dashboard.*` keys are enabled on this franchise and consulted by nothing in either codebase:
 * `dashboard.alerts`, `dashboard.kpi_today` and `dashboard.live_orders`.
 */

export function DashboardPage() {
  const { user, hasPermission } = useAuth()
  const { isModuleEnabled } = useFranchiseModules()

  const [period, setPeriod] = useState<PeriodKey>('dnes')
  const [custom, setCustom] = useState(() => periodRange('dnes'))

  const range = period === 'vlastny' ? custom : periodRange(period)
  const multiDay = isMultiDay(range)
  const backwards = rangeIsBackwards(range)
  const params = { date_from: range.from, date_to: range.to }

  // FB-172: the sidebar's flag is not the endpoints'. Asking anyway would be three 403s and an empty screen.
  const allowed = mayReadDashboard(user?.systemRole, hasPermission)
  const enabled = allowed && !backwards

  const statsQuery = useGetDashboardStats(params, { query: { enabled } })
  const chartQuery = useGetDashboardRevenueChart(params, { query: { enabled } })
  const dailyQuery = useGetDashboardDaily(params, { query: { enabled: enabled && multiDay } })

  const stats = useMemo(() => statsOf(statsQuery.data), [statsQuery.data])
  const chart = useMemo(() => chartOf(chartQuery.data), [chartQuery.data])
  const days = useMemo(() => dailyAscending(dailyOf(dailyQuery.data)), [dailyQuery.data])

  const setCustomEnd = (which: 'from' | 'to', value: string) => {
    setCustom({ ...(period === 'vlastny' ? custom : range), [which]: value })
    setPeriod('vlastny')
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={tt('Prehľad')}
        description={tt('Tržby, objednávky a platby za zvolené obdobie.')}
        icon={<TrendingUp className="w-5 h-5" />}
      />

      {/* ─── the filter bar ─── */}
      <div className="bg-card border border-border rounded-2xl p-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-muted-foreground" />
          <span className="font-semibold" data-range-label>
            {rangeLabel(period, range)}
          </span>
          {multiDay && !backwards && (
            <span className="text-xs text-muted-foreground" data-range-days>
              {formatCount(rangeDays(range))} <T>dní</T>
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1.5">
            {QUICK_PERIODS.map((key) => (
              <button
                key={key}
                type="button"
                data-period={key}
                data-period-active={period === key ? 'yes' : 'no'}
                onClick={() => setPeriod(key)}
                className={cn(
                  'px-3 py-1.5 rounded-lg text-xs font-semibold transition-all',
                  period === key
                    ? 'bg-primary text-primary-foreground shadow'
                    : 'bg-background border border-border text-muted-foreground hover:text-foreground',
                )}
              >
                {periodLabel(key)}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={period === 'vlastny' ? custom.from : range.from}
              onChange={(event) => setCustomEnd('from', event.target.value)}
              aria-label={tt('Od dátumu')}
              data-custom-from
              className="bg-background border border-border rounded-lg px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-primary/30"
            />
            <span className="text-muted-foreground text-xs">-</span>
            <input
              type="date"
              value={period === 'vlastny' ? custom.to : range.to}
              onChange={(event) => setCustomEnd('to', event.target.value)}
              aria-label={tt('Do dátumu')}
              data-custom-to
              className="bg-background border border-border rounded-lg px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>
      </div>

      {/* FB-172 - said instead of showing an empty screen. */}
      {!allowed && (
        <div
          className="bg-card border border-amber-500/40 rounded-2xl p-5 flex items-start gap-3"
          data-refused
        >
          <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-sm">
              <T>Čísla vám server nevydá</T>
            </p>
            <p className="text-xs text-muted-foreground mt-1">{refusalNote(user?.systemRole)}</p>
          </div>
        </div>
      )}

      {/* Two date inputs and nothing stopping them crossing. The server answers zeroes, not an error. */}
      {allowed && backwards && (
        <p className="flex items-start gap-1.5 text-xs text-red-500" data-backwards>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <T>Dátum „od“ je neskorší ako „do“ - v takom rozsahu nie je ani jeden deň.</T>{' '}
            {formatRangeDate(range.from)} → {formatRangeDate(range.to)}
          </span>
        </p>
      )}

      {allowed && !backwards && (
        <>
          {statsQuery.isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {Array.from({ length: 12 }, (_, index) => (
                <div
                  key={index}
                  className="bg-card h-28 rounded-2xl animate-pulse border border-border"
                />
              ))}
            </div>
          ) : stats === null ? (
            <EmptyState
              icon={<TrendingUp className="w-8 h-8" />}
              title={tt('Čísla sa nepodarilo načítať')}
              description={tt('Skúste obdobie zvoliť znova; server na túto otázku neodpovedal.')}
            />
          ) : (
            <>
              {reconciliationNote(stats) !== '' && (
                <p className="flex items-start gap-1.5 text-xs text-amber-500" data-reconciliation>
                  <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
                  {reconciliationNote(stats)}
                </p>
              )}
              <KpiCards stats={stats} isModuleEnabled={isModuleEnabled} />
            </>
          )}

          {isModuleEnabled('dashboard.chart_revenue') && (
            <section className="bg-card border border-border rounded-2xl p-6" data-chart>
              <h3 className="font-bold text-lg mb-1">
                {multiDay ? tt('Tržby podľa dňa') : tt('Tržby podľa hodiny')}
              </h3>
              <p className="text-sm text-muted-foreground mb-5">
                {multiDay
                  ? `${formatRangeDate(range.from)} - ${formatRangeDate(range.to)}`
                  : `${formatRangeDate(range.from)} · ${tt('Rozvoz + Reštaurácia + BAR')}`}
              </p>
              {chartQuery.isLoading ? (
                <div className="h-72 flex items-center justify-center">
                  <Spinner />
                </div>
              ) : chartIsEmpty(chart) ? (
                <div className="h-72 flex items-center justify-center" data-chart-empty>
                  <EmptyState
                    icon={<TrendingUp className="w-8 h-8" />}
                    title={tt('V tomto období sa nič nepredalo')}
                    description={tt('Prázdny graf nie je porucha - v tomto rozsahu nie je ani jedna objednávka.')}
                  />
                </div>
              ) : (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chart} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="dashDelivery" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="dashRestaurant" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.35} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="dashBar" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.35} />
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                      <XAxis
                        dataKey="bucket"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                        dy={8}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                        tickFormatter={(value: number) => formatCents(value)}
                        dx={-4}
                        width={72}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          borderRadius: 12,
                          border: '1px solid hsl(var(--border))',
                        }}
                        // recharts types the value as `ValueType | undefined`; the series are all cents.
                        formatter={(value, name) => [
                          formatCents(typeof value === 'number' ? value : 0),
                          String(name),
                        ]}
                        labelStyle={{ color: 'hsl(var(--foreground))', fontWeight: 600 }}
                      />
                      <Legend iconType="circle" wrapperStyle={{ paddingTop: 16, fontSize: 12 }} />
                      <Area
                        type="monotone"
                        dataKey="deliveryCents"
                        name={tt('Rozvoz')}
                        stroke="hsl(var(--primary))"
                        strokeWidth={2.5}
                        fill="url(#dashDelivery)"
                      />
                      <Area
                        type="monotone"
                        dataKey="restaurantCents"
                        name={tt('Reštaurácia')}
                        stroke="#3b82f6"
                        strokeWidth={2.5}
                        fill="url(#dashRestaurant)"
                      />
                      <Area
                        type="monotone"
                        dataKey="barCents"
                        name={tt('BAR')}
                        stroke="#f59e0b"
                        strokeWidth={2.5}
                        fill="url(#dashBar)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </section>
          )}

          <div className={cn('grid gap-6', multiDay ? 'grid-cols-1 xl:grid-cols-3' : 'grid-cols-1 lg:grid-cols-2')}>
            {stats !== null && isModuleEnabled('dashboard.payment_methods_split') && (
              <Breakdown stats={stats} />
            )}

            {multiDay && isModuleEnabled('dashboard.daily_table') && (
              <DayTable
                days={days}
                stats={stats}
                loading={dailyQuery.isLoading}
                today={periodRange('dnes').from}
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}

// ─── the cards ───────────────────────────────────────────────────────────────

function KpiCards({
  stats,
  isModuleEnabled,
}: {
  stats: Stats
  isModuleEnabled: (key: string) => boolean
}) {
  const total = stats.totalRevenueCents
  const discount = discountShare(stats)

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-kpis>
      {isModuleEnabled('dashboard.kpi.revenue') && (
        <StatCard
          label={tt('Celková tržba')}
          value={formatCents(total)}
          icon={Banknote}
          color="primary"
          sub={`${formatCount(stats.totalOrders)} ${tt('objednávok')}`}
        />
      )}
      {isModuleEnabled('dashboard.kpi.orders') && (
        <StatCard
          label={tt('Počet objednávok')}
          value={formatCount(stats.totalOrders)}
          icon={ShoppingBag}
          color="blue"
          sub={`Ø ${formatCents(stats.averageOrderValueCents)}/${tt('obj.')}`}
        />
      )}
      {isModuleEnabled('dashboard.kpi.aov') && (
        <StatCard
          label={tt('Priemerná objednávka')}
          value={formatCents(stats.averageOrderValueCents)}
          icon={TrendingUp}
          color="emerald"
          sub={tt('na objednávku')}
        />
      )}
      {isModuleEnabled('dashboard.kpi.discounts') && (
        <StatCard
          label={tt('Zľavy / Kupóny')}
          value={formatCents(stats.discountsCents)}
          icon={Percent}
          color="orange"
          // Against what was billed, not what was banked — the denominator legacy also uses here.
          sub={discount === null ? tt('nemerané') : `${formatPercent(discount, 1)} ${tt('z účtov')}`}
        />
      )}
      {isModuleEnabled('dashboard.kpi.cancellations') && (
        <StatCard
          label={tt('Storno objednávok')}
          value={formatCount(stats.cancelledOrders)}
          icon={XCircle}
          color={stats.cancelledOrders > 0 ? 'red' : 'emerald'}
          sub={stats.cancelledOrders > 0 ? tt('zrušených') : tt('žiadne storno')}
        />
      )}

      {/* FB-174 - the channels the server names, however many it names. */}
      {isModuleEnabled('dashboard.kpi.channels') &&
        channelsOf(stats).map((channel) => (
          <StatCard
            key={channel.slug}
            label={channel.name}
            value={formatCents(channel.revenueCents)}
            icon={channel.slug === 'delivery' ? Truck : channel.slug === 'restaurant' ? Store : ShoppingBag}
            color={channel.slug === 'delivery' ? 'violet' : channel.slug === 'restaurant' ? 'blue' : 'amber'}
            sub={
              channel.orders > 0
                ? `${shareLabel(channel.revenueCents, total)} · ${formatCount(channel.orders)} ${tt('obj.')}`
                : shareLabel(channel.revenueCents, total)
            }
            className={`channel-${channel.slug}`}
          />
        ))}

      {isModuleEnabled('dashboard.kpi.payments') &&
        paymentsOf(stats).map((payment) => (
          <StatCard
            key={payment.key}
            label={payment.label}
            value={formatCents(payment.cents)}
            icon={payment.key === 'cash' ? Banknote : CreditCard}
            color={payment.key === 'cash' ? 'emerald' : payment.key === 'courier' ? 'orange' : 'blue'}
            sub={shareLabel(payment.cents, total)}
          />
        ))}

      {/* §9.413 - **the fee tile is gone, by the owner's instruction**: *"see only náklady na rozvoz
        * there ... poplatky za rozvoz can be hidden."*
        *
        * Removed rather than commented out, because a commented-out tile is a decision the next
        * reader has to make again. `SUM(orders.delivery_cost)` is still on the payload as
        * `deliveryCost` - §10A.187 left it untouched - so showing it again is a JSX change and not
        * a server one.
        *
        * Kept for the record, because it is what FB-733 was about: that figure is the fee
        * **charged** to customers, it arrives with the order and goes onto the receipt as a
        * positive line, and it was labelled *Náklady na rozvoz* until §9.411. Legacy declares the
        * field in its stats type and draws no tile, so the name was chosen here and no parity check
        * could have found it. */}

      {/* §9.412, owner decision - *"so do it to use these vyplaty, nakupy etc."* Payouts, purchases
        * and fuel out of the handovers, which is what delivery actually cost; the tile above is the
        * fee it charged.
        *
        * **The note is the point.** I recommended against this tile because these figures are per
        * shift and appear only when a driver settles up, while everything else here is per order and
        * live - so it reads 0,00 all morning and jumps at closing. The owner decided otherwise, so
        * the caveat is on the screen rather than in a refusal, and it shows **even at nought**:
        * 0,00 with no explanation is exactly the state that invites the report. */}
      {isModuleEnabled('dashboard.kpi.channels') && (
        <StatCard
          label={tt('Náklady na rozvoz')}
          // §9.413 - a dash when nobody has handed over, a figure when somebody has. The decision
          // is in `deliveryExpenseTile` so it can be proved by reverting; a ternary in here is
          // reachable only by rendering the whole dashboard.
          value={deliveryExpenseTile(stats).value}
          icon={Wallet}
          color="red"
          sub={deliveryExpenseTile(stats).sub}
          className="delivery-expense"
        />
      )}
    </div>
  )
}

function Breakdown({ stats }: { stats: Stats }) {
  const total = stats.totalRevenueCents

  return (
    <section className="bg-card border border-border rounded-2xl p-6 space-y-5" data-breakdown>
      <h3 className="font-bold text-base">
        <T>Platobné metódy</T>
      </h3>
      {paymentsOf(stats).map((payment) => {
        const share = total > 0 ? (payment.cents / total) * 100 : 0
        return (
          <div key={payment.key} data-payment={payment.key}>
            <div className="flex justify-between text-sm mb-1.5">
              <span className="text-muted-foreground">{payment.label}</span>
              <span className="font-bold">{formatCents(payment.cents)}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
              <div
                className={cn(
                  'h-2 rounded-full transition-all',
                  payment.key === 'cash'
                    ? 'bg-emerald-500'
                    : payment.key === 'card'
                      ? 'bg-indigo-500'
                      : payment.key === 'courier'
                        ? 'bg-orange-500'
                        : 'bg-amber-500',
                )}
                style={{ width: `${Math.min(share, 100)}%` }}
              />
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 text-right">
              {shareLabel(payment.cents, total)}
            </p>
          </div>
        )
      })}

      <div className="pt-2 border-t border-border space-y-2 text-sm">
        {channelsOf(stats).map((channel) => (
          <div key={channel.slug} className="flex justify-between" data-channel={channel.slug}>
            <span className="text-muted-foreground flex items-center gap-2">
              {channel.slug === 'delivery' ? (
                <Truck className="w-4 h-4 text-primary" />
              ) : channel.slug === 'restaurant' ? (
                <Store className="w-4 h-4 text-blue-500" />
              ) : (
                <ShoppingBag className="w-4 h-4 text-amber-500" />
              )}
              {channel.name}
            </span>
            <span className="font-bold">{formatCents(channel.revenueCents)}</span>
          </div>
        ))}
        <div className="flex justify-between">
          <span className="text-muted-foreground flex items-center gap-2">
            <Receipt className="w-4 h-4 text-orange-400" /> <T>Zľavy</T>
          </span>
          <span className="font-bold text-orange-400">−{formatCents(stats.discountsCents)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground flex items-center gap-2">
            <XCircle className="w-4 h-4 text-red-400" /> <T>Storno</T>
          </span>
          <span className="font-bold text-red-400">
            {formatCount(stats.cancelledOrders)} <T>obj.</T>
          </span>
        </div>
      </div>
    </section>
  )
}

function DayTable({
  days,
  stats,
  loading,
  today,
}: {
  days: DayRow[]
  stats: Stats | null
  loading: boolean
  today: string
}) {
  const mismatch = dailyMismatchNote(days, stats)

  return (
    <section className="xl:col-span-2 bg-card border border-border rounded-2xl overflow-hidden" data-day-table>
      <div className="px-5 py-4 border-b border-border">
        <h3 className="font-bold text-base">
          <T>Prehľad podľa dní</T>
        </h3>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          {/* The endpoint answers newest-first; sorted so the table runs the same way as the chart. */}
          <T>Zoradené od najstaršieho dňa, rovnako ako graf vyššie.</T>
        </p>
      </div>

      {mismatch !== '' && (
        <p className="px-5 py-2 text-xs text-amber-500 border-b border-border" data-day-mismatch>
          {mismatch}
        </p>
      )}

      {loading ? (
        <div className="p-6 flex justify-center">
          <Spinner />
        </div>
      ) : days.length === 0 ? (
        <EmptyState
          icon={<CalendarDays className="w-8 h-8" />}
          title={tt('Žiadne dni')}
          description={tt('V tomto rozsahu server nevrátil ani jeden deň.')}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-background/30 text-muted-foreground text-xs uppercase tracking-wide">
                <th className="text-left px-4 py-3 font-semibold">
                  <T>Dátum</T>
                </th>
                <th className="text-right px-4 py-3 font-semibold">
                  <T>Tržba</T>
                </th>
                <th className="text-right px-4 py-3 font-semibold">
                  <T>Obj.</T>
                </th>
                {/* FB-173 - the split legacy fetches per day and never draws. */}
                <th className="text-right px-4 py-3 font-semibold hidden lg:table-cell">
                  <T>R / Reš / BAR</T>
                </th>
                <th className="text-right px-4 py-3 font-semibold hidden sm:table-cell">Ø obj.</th>
                <th className="text-right px-4 py-3 font-semibold hidden md:table-cell">
                  <T>Zľavy</T>
                </th>
                <th className="text-right px-4 py-3 font-semibold hidden md:table-cell">
                  <T>Storno</T>
                </th>
                <th className="px-4 py-3 hidden lg:table-cell" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {days.map((row) => {
                const [, month, day] = row.date.split('-')
                const isToday = row.date === today
                return (
                  <tr
                    key={row.date}
                    data-day={row.date}
                    className={cn('hover:bg-card/80 transition-colors', isToday && 'bg-primary/5')}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isToday && <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />}
                        <span className="font-semibold">
                          {day}.{month}.
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className={cn(
                          'font-bold',
                          row.totalRevenueCents === 0 && 'text-muted-foreground/40',
                        )}
                      >
                        {formatCents(row.totalRevenueCents)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={cn(row.totalOrders === 0 && 'text-muted-foreground/40')}>
                        {formatCount(row.totalOrders)}
                      </span>
                    </td>
                    <td
                      className="px-4 py-3 text-right hidden lg:table-cell text-xs text-muted-foreground tabular-nums"
                      data-day-split
                    >
                      {formatCount(row.deliveryOrders)} / {formatCount(row.restaurantOrders)} /{' '}
                      {formatCount(row.barOrders)}
                    </td>
                    <td className="px-4 py-3 text-right hidden sm:table-cell text-xs text-muted-foreground">
                      {row.totalOrders > 0 ? formatCents(row.averageOrderValueCents) : '-'}
                    </td>
                    <td className="px-4 py-3 text-right hidden md:table-cell text-xs">
                      <span className={row.discountsCents > 0 ? 'text-orange-400' : 'text-muted-foreground/30'}>
                        {row.discountsCents > 0 ? `−${formatCents(row.discountsCents)}` : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right hidden md:table-cell text-xs">
                      <span className={row.cancelledOrders > 0 ? 'text-red-400' : 'text-muted-foreground/30'}>
                        {row.cancelledOrders > 0 ? formatCount(row.cancelledOrders) : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell w-28">
                      <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                        <div
                          className="bg-primary h-1.5 rounded-full transition-all"
                          style={{ width: `${dayBarPercent(row, days)}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
            {stats !== null && days.length > 1 && (
              <tfoot>
                <tr className="border-t-2 border-border bg-card/80 font-bold" data-day-total>
                  <td className="px-4 py-3 text-xs uppercase tracking-wide">
                    <T>Spolu</T>
                  </td>
                  <td className="px-4 py-3 text-right text-primary">
                    {formatCents(stats.totalRevenueCents)}
                  </td>
                  <td className="px-4 py-3 text-right">{formatCount(stats.totalOrders)}</td>
                  <td className="px-4 py-3 text-right hidden lg:table-cell text-xs text-muted-foreground">
                    {formatCents(busiestDayCents(days))} <T>najlepší deň</T>
                  </td>
                  <td className="px-4 py-3 text-right hidden sm:table-cell text-xs text-muted-foreground">
                    {formatCents(stats.averageOrderValueCents)}
                  </td>
                  <td className="px-4 py-3 text-right hidden md:table-cell text-orange-400 text-xs">
                    {stats.discountsCents > 0 ? `−${formatCents(stats.discountsCents)}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-right hidden md:table-cell text-red-400 text-xs">
                    {stats.cancelledOrders > 0 ? formatCount(stats.cancelledOrders) : '-'}
                  </td>
                  <td className="hidden lg:table-cell" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </section>
  )
}
